import Phaser from 'phaser';
import type { GlyphName, HeroClassId } from '../core';

const CLASS_GLYPH: Record<HeroClassId, GlyphName> = {
  guardian: 'shield',
  infiltration: 'root',
  stealth: 'moon',
  tidecaller: 'wave',
  grove: 'sprout',
};

export function classGlyph(id: HeroClassId): GlyphName {
  return CLASS_GLYPH[id];
}

const INK = 0x3b2414;
const HONEY = 0xc4842a;
const MOSS = 0x2f6b3a;

function stamp(scene: Phaser.Scene, key: string, draw: (g: Phaser.GameObjects.Graphics) => void): void {
  if (scene.textures.exists(key)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  draw(g);
  g.generateTexture(key, 64, 64);
  g.destroy();
}

export function glyphKey(glyph: GlyphName): string {
  return `glyph-${glyph}`;
}

export function trickIconKey(id: string): string {
  return `trick-icon-${id}`;
}

/** Painted still when it has loaded. The ink glyph covers a missing file. */
export function trickFaceKey(scene: Phaser.Scene, id: string, glyph: GlyphName): string {
  const key = trickIconKey(id);
  return scene.textures.exists(key) ? key : glyphKey(glyph);
}

/** Dark placeholder glyphs. One texture per shape, shared by cards and class pills. */
export function ensureGlyphs(scene: Phaser.Scene): void {
  const names: GlyphName[] = [
    'plus', 'dots', 'drop', 'line', 'mail', 'ring', 'swap', 'mask', 'moon', 'trail',
    'crack', 'coin', 'root', 'bomb', 'shield', 'burst', 'heart', 'bolt', 'wave',
    'bubble', 'rain', 'falls', 'sprout', 'bloom',
  ];
  for (const name of names) stamp(scene, glyphKey(name), (g) => drawGlyph(g, name));
}

function drawGlyph(g: Phaser.GameObjects.Graphics, glyph: GlyphName): void {
  g.fillStyle(INK, 1);
  g.lineStyle(4, INK, 1);
  switch (glyph) {
    case 'plus':
      g.fillRoundedRect(26, 8, 12, 48, 4);
      g.fillRoundedRect(8, 26, 48, 12, 4);
      break;
    case 'dots':
      g.fillCircle(16, 18, 6);
      g.fillCircle(34, 14, 5);
      g.fillCircle(48, 28, 6);
      g.fillCircle(22, 40, 7);
      g.fillCircle(42, 48, 5);
      break;
    case 'drop':
      g.fillTriangle(32, 6, 14, 34, 50, 34);
      g.fillCircle(32, 40, 16);
      g.fillStyle(0xfff6e4, 1);
      g.fillCircle(24, 36, 4);
      break;
    case 'line':
      g.fillRoundedRect(6, 26, 52, 12, 6);
      g.fillStyle(HONEY, 1);
      g.fillCircle(16, 32, 4);
      g.fillCircle(32, 32, 4);
      g.fillCircle(48, 32, 4);
      break;
    case 'mail':
      g.fillRoundedRect(8, 16, 48, 32, 6);
      g.lineStyle(3, 0xfff6e4, 1);
      g.lineBetween(12, 20, 32, 36);
      g.lineBetween(52, 20, 32, 36);
      break;
    case 'ring':
      g.lineStyle(8, INK, 1);
      g.strokeCircle(32, 32, 16);
      g.fillStyle(HONEY, 1);
      g.fillCircle(32, 14, 5);
      break;
    case 'swap':
      g.lineStyle(5, INK, 1);
      g.lineBetween(10, 22, 48, 22);
      g.lineBetween(16, 42, 54, 42);
      g.fillTriangle(44, 14, 56, 22, 44, 30);
      g.fillTriangle(20, 34, 8, 42, 20, 50);
      break;
    case 'mask':
      g.fillEllipse(32, 34, 36, 28);
      g.fillStyle(0xfff6e4, 1);
      g.fillCircle(22, 32, 5);
      g.fillCircle(42, 32, 5);
      g.fillTriangle(32, 36, 26, 48, 38, 48);
      break;
    case 'moon':
      g.fillCircle(28, 32, 18);
      g.fillStyle(0xfff6e4, 1);
      g.fillCircle(36, 26, 14);
      break;
    case 'trail':
      g.fillCircle(14, 46, 5);
      g.fillCircle(26, 34, 6);
      g.fillCircle(38, 22, 7);
      g.fillCircle(50, 12, 5);
      break;
    case 'crack':
      g.lineStyle(5, INK, 1);
      g.lineBetween(18, 8, 28, 24);
      g.lineBetween(28, 24, 16, 32);
      g.lineBetween(16, 32, 36, 56);
      g.lineBetween(28, 24, 46, 18);
      break;
    case 'coin':
      g.fillCircle(32, 32, 18);
      g.fillStyle(HONEY, 1);
      g.fillCircle(32, 32, 12);
      g.fillStyle(INK, 1);
      g.fillRect(29, 22, 6, 20);
      break;
    case 'root':
      g.fillRoundedRect(28, 8, 8, 22, 3);
      g.lineStyle(5, INK, 1);
      g.lineBetween(32, 28, 14, 50);
      g.lineBetween(32, 28, 50, 48);
      g.lineBetween(32, 36, 32, 56);
      break;
    case 'bomb':
      g.fillCircle(32, 38, 16);
      g.fillRect(28, 16, 8, 10);
      g.lineStyle(3, HONEY, 1);
      g.lineBetween(36, 16, 46, 8);
      break;
    case 'shield':
      g.fillTriangle(32, 8, 12, 18, 52, 18);
      g.fillRoundedRect(12, 18, 40, 22, 4);
      g.fillTriangle(12, 36, 52, 36, 32, 58);
      g.fillStyle(0xfff6e4, 1);
      g.fillRect(28, 22, 8, 18);
      break;
    case 'burst':
      g.fillCircle(32, 32, 8);
      for (let i = 0; i < 8; i++) {
        const angle = (Math.PI * 2 * i) / 8;
        g.fillCircle(32 + Math.cos(angle) * 20, 32 + Math.sin(angle) * 20, 4);
      }
      break;
    case 'heart':
      g.fillCircle(22, 26, 10);
      g.fillCircle(42, 26, 10);
      g.fillTriangle(12, 30, 52, 30, 32, 54);
      break;
    case 'bolt':
      g.fillTriangle(36, 4, 14, 34, 30, 34);
      g.fillTriangle(28, 30, 50, 30, 24, 60);
      break;
    case 'wave':
      g.lineStyle(5, INK, 1);
      g.beginPath();
      g.moveTo(6, 24);
      g.lineTo(18, 16);
      g.lineTo(30, 24);
      g.lineTo(42, 16);
      g.lineTo(58, 24);
      g.strokePath();
      g.beginPath();
      g.moveTo(6, 42);
      g.lineTo(18, 34);
      g.lineTo(30, 42);
      g.lineTo(42, 34);
      g.lineTo(58, 42);
      g.strokePath();
      break;
    case 'bubble':
      g.lineStyle(4, INK, 1);
      g.strokeCircle(26, 36, 14);
      g.strokeCircle(44, 24, 10);
      g.fillCircle(20, 30, 3);
      break;
    case 'rain':
      g.fillTriangle(16, 10, 10, 28, 22, 28);
      g.fillCircle(16, 34, 7);
      g.fillTriangle(36, 16, 30, 36, 42, 36);
      g.fillCircle(36, 42, 8);
      g.fillTriangle(52, 8, 48, 22, 56, 22);
      g.fillCircle(52, 26, 5);
      break;
    case 'falls':
      g.fillRoundedRect(22, 6, 20, 10, 4);
      g.fillRect(26, 16, 5, 36);
      g.fillRect(34, 16, 5, 28);
      g.fillCircle(28, 54, 4);
      g.fillCircle(36, 48, 3);
      break;
    case 'sprout':
      g.fillStyle(MOSS, 1);
      g.fillEllipse(22, 28, 18, 12);
      g.fillEllipse(42, 26, 16, 11);
      g.fillStyle(INK, 1);
      g.fillRoundedRect(30, 28, 5, 24, 2);
      break;
    case 'bloom':
      g.fillStyle(0xc45a8a, 1);
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 * i) / 6;
        g.fillCircle(32 + Math.cos(angle) * 14, 32 + Math.sin(angle) * 14, 8);
      }
      g.fillStyle(HONEY, 1);
      g.fillCircle(32, 32, 6);
      break;
    default:
      g.fillCircle(32, 32, 8);
  }
}
